import * as angular from "angular";
import * as _ from "lodash";
import { DocumentService } from "../services/document.service";
import { fileConstants } from "../utils/constants/files.constants";
import { FileType } from "../utils/interfaces";

export class DocumentModel {
    public readonly requestIgnoreList = ["downloadUrl"];

    public dccid?: string | null;
    public dccompleted?: string | null;
    public dcfilename?: string | null;
    public dcfilesize?: string | null;
    public dcid?: string | null;
    public dcidrand?: string | null;
    public dcoldfilename?: string | null;
    public dcstatus?: string | null;
    public dctimestamp?: string | null;
    public dctype?: string | null;
    public expcreation?: string | null;
    public expid?: string | null;
    public exppid?: string | null;
    public expprogress?: string | null;
    public exptimestamp?: string | null;
    public show?: boolean;

    public resource: DocumentService;
    public $q: any;
    public downloadUrl = "";
    // KML URL
    public url = "";
    public thumbnailImage = "";

    constructor(data: any, DocumentService: DocumentService, $q: any) {
        this.resource = DocumentService;
        this.$q = $q;
        this.setData(data);
    }

    setData(data: any): void {
        _.merge(this, data);
    }

    static thumbnailForItem(extension: string): string {
        let thumbnailImage = null;
        angular.forEach(fileConstants.acceptedFileTypes, (value: FileType) => {
            if (
                value.types &&
                extension &&
                value.types.indexOf(extension.toLowerCase()) > -1
            ) {
                thumbnailImage = value.imgpath;
            }
        });
        if (!thumbnailImage) {
            return "/img/filetypes/none.png";
        }
        return thumbnailImage;
    }

    inProgress(): boolean {
        return this.dccompleted != "1";
    }

    thumbnail(): string {
        if (this.thumbnailImage != "") return this.thumbnailImage;
        // Shapefile exports are delivered as a .zip, so give them the geo icon
        // instead of the generic archive icon. Gate on the zip type as well as
        // the name so a non-shapefile export that merely happens to be named
        // "...shapefile..." keeps its own icon.
        const name = (
            this.dcoldfilename ||
            this.dcfilename ||
            ""
        ).toLowerCase();
        if (this.dctype == "zip" && name.indexOf("shapefile") > -1) {
            this.thumbnailImage = "/img/filetypes/geojson.svg";
        } else {
            this.thumbnailImage = DocumentModel.thumbnailForItem(this.dctype);
        }
        return this.thumbnailImage;
    }

    getUrl(): Promise<any> {
        const deferred = this.$q.defer();
        this.getUrlWithID(this.dcid, true).then(
            (url: string) => {
                this.url = url;
                deferred.resolve(this);
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getDownloadUrl(): Promise<any> {
        const deferred = this.$q.defer();
        this.getUrlWithID(this.dcid).then(
            (url: string) => {
                this.downloadUrl = url;
                deferred.resolve(this);
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getData(): Promise<any> {
        const deferred = this.$q.defer();
        this.getDataWithID(this.dcid).then(
            (data: object) => {
                deferred.resolve(data);
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    isViewable(): boolean {
        return (
            this.dctype != undefined &&
            (fileConstants.viewableFileTypes as readonly string[]).indexOf(
                this.dctype.toLowerCase()
            ) > -1
        );
    }

    viewablePath(): string {
        if (this.isViewable()) {
            return this.downloadUrl;
        } else {
            return this.thumbnail();
        }
    }

    getUrlWithID(dcId: string, inline: boolean = false): Promise<any> {
        const deferred = this.$q.defer();
        this.resource.get_url({ id: dcId, inline: inline }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.url);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getDataWithID(dcId: string, inline: boolean = false): Promise<any> {
        const deferred = this.$q.defer();
        this.resource.get_data({ id: dcId, inline: inline }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }
}
